import { Link, Stack, Typography } from '@mui/material';
import { VerifiedCompanyIcon } from 'components/common/icon/VerifiedCompanyIcon';
import { MRT_ColumnDef } from 'material-react-table';
import { Link as RouterLink } from 'react-router-dom';
import {
  IngredientIdentifier,
  SearchDocument,
  SearchTarget
} from 'types/Search';
import { useAuth } from 'react-oidc-context';
import { useMemo } from 'react';
import { BOOKMARK_SIZE } from '../types';
import { useTranslation } from 'react-i18next';
import { ProductType } from 'types/Products';
import { SegmentTag } from 'types/global';
import {
  getIngredientIdentifier,
  getLegacyAttributes,
  bookmarkField,
  getDefaultFields
} from './AttributesDefinitions';
import { useFlags } from 'launchdarkly-react-client-sdk';

export const useCovaloColumns = (
  searchTarget: SearchTarget,
  segment: SegmentTag,
  eventKey?: string
): MRT_ColumnDef<SearchDocument>[] => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { searchTableColumnsConfiguration } = useFlags();

  return useMemo(
    () => [
      ...(isAuthenticated
        ? [bookmarkField]
        : [{ accessorKey: 'placeholder', header: '', size: BOOKMARK_SIZE }]),

      {
        accessorKey: 'name',
        enableHiding: false,
        enableColumnOrdering: false,
        visibleInShowHideMenu: false,
        header: t(`products.types.singular.${searchTarget}`),
        Cell: ({ renderedCellValue }) => (
          <Typography variant="body2" sx={{ fontWeight: '500' }}>
            {renderedCellValue}
          </Typography>
        )
      },

      ...(searchTableColumnsConfiguration
        ? isAuthenticated
          ? getDefaultFields(searchTarget, segment)
          : searchTarget === ProductType.INGREDIENTS &&
            segment === SegmentTag.PERSONALCARE
          ? [getIngredientIdentifier(IngredientIdentifier.INCI)]
          : []
        : []),

      ...(!searchTableColumnsConfiguration || segment === SegmentTag.FOOD
        ? [getLegacyAttributes(segment === SegmentTag.FOOD)]
        : []),
      {
        accessorKey: 'company.name',
        header: 'Company',
        enableColumnOrdering: false,
        enableHiding: false,
        visibleInShowHideMenu: false,
        accessorFn: row => row.company.name,
        Cell: ({ row, renderedCellValue }) => (
          <Stack direction={'row'} sx={{ alignItems: 'center', gap: 1 }}>
            <Link
              component={RouterLink}
              onClick={e => e.stopPropagation()}
              sx={{
                color: ({ palette }) => palette.text.primary,
                textDecoration: 'none',
                '&:hover': { color: 'primary.main' }
              }}
              to={
                eventKey
                  ? `/events/${eventKey}/company/${row.original.company.key}`
                  : `/company/${row.original.company.key}`
              }
            >
              {renderedCellValue}
            </Link>
            {row.original.company.verified && <VerifiedCompanyIcon size="sm" />}
          </Stack>
        )
      }
    ],
    [
      isAuthenticated,
      t,
      searchTarget,
      searchTableColumnsConfiguration,
      segment,
      eventKey
    ]
  );
};
